import React, {useState, useEffect} from 'react';
import {clasesAPI, profesoresAPI, asignaturasAPI, instrumentosAPI, clasesAlumnosAPI} from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import Modal from '../Common/Modal';
import ClassForm from '../Forms/ClassForm';
import SearchFilter from '../Common/SearchFilter';
import DataTable from '../Common/DataTable';
import {PencilIcon, TrashIcon, PlusIcon, DocumentDuplicateIcon} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {getDayName, formatTime, filterBySearchTerm} from '../../services/utils';
import {useAuth} from '../../context/AuthContext';

const Classes = ({currentYear}) => {
    const {isReadOnly} = useAuth();
    const [classes, setClasses] = useState([]);
    const [filteredClasses, setFilteredClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [selectedClass, setSelectedClass] = useState(null);
    const [classToDuplicate, setClassToDuplicate] = useState(null);
    const [professors, setProfessors] = useState({});
    const [subjects, setSubjects] = useState({});
    const [instruments, setInstruments] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchData();
    }, [currentYear]);

    const parseTimeToMinutes = (timeString) => {
        if (!timeString) 
            return null;
        const [hours, minutes] = timeString
            .split(':')
            .map(Number);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) 
            return null;
        return hours * 60 + minutes;
    };

    const hasTeacherScheduleConflict = (
        ProfesorID,
        DiaSemana,
        H_Inicio,
        H_Final,
        excludeClassId = null
    ) => {
        const newStart = parseTimeToMinutes(H_Inicio);
        const newEnd = parseTimeToMinutes(H_Final);

        if (newStart === null || newEnd === null) 
            return false;
        
        return classes.some(existingClass => {
            if (existingClass.ProfesorID !== Number(ProfesorID)) 
                return false;
            if (existingClass.DiaSemana !== DiaSemana) 
                return false;
            if (excludeClassId && existingClass.IDClase === excludeClassId) 
                return false;
            
            const existingStart = parseTimeToMinutes(existingClass.H_Inicio);
            const existingEnd = parseTimeToMinutes(existingClass.H_Final);

            if (existingStart === null || existingEnd === null) 
                return false;
            
            return newStart < existingEnd && newEnd > existingStart;
        });
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const [classesRes, professorsRes, subjectsRes, instrumentsRes] = await Promise.all(
                [clasesAPI.getByYear(currentYear), profesoresAPI.getAll(), asignaturasAPI.getAll(), instrumentosAPI.getAll()]
            );

            setClasses(classesRes.data);

            // Crear mapas para búsqueda rápida
            const professorsMap = {};
            professorsRes
                .data
                .forEach(prof => {
                    professorsMap[prof.IDProfesor] = `${prof.Nombre} ${prof.Apellido}`;
                });
            setProfessors(professorsMap);

            const subjectsMap = {};
            subjectsRes
                .data
                .forEach(subject => {
                    subjectsMap[subject.IDAsignatura] = subject.Nombre;
                });
            setSubjects(subjectsMap);

            const instrumentsMap = {};
            instrumentsRes
                .data
                .forEach(instrument => {
                    instrumentsMap[instrument.IDInstrumento] = instrument.Nombre;
                });
            setInstruments(instrumentsMap);
            setFilteredClasses(filterClassesByTerm(classesRes.data, searchTerm, professorsMap, subjectsMap, instrumentsMap));

            setError(null);
        } catch (err) {
            setError('Error al cargar los datos');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterClassesByTerm = (
        classesData,
        term,
        professorsMap = professors,
        subjectsMap = subjects,
        instrumentsMap = instruments
    ) => {
        return filterBySearchTerm(classesData, term, (clase, lowerTerm) => {
            const professorName = professorsMap[clase.ProfesorID]
                ?.toLowerCase() || '';
            const subjectName = subjectsMap[clase.AsignaturaID]
                ?.toLowerCase() || '';
            const instrumentName = instrumentsMap[clase.InstrumentoID]
                ?.toLowerCase() || '';

            return (
                professorName.includes(lowerTerm) || subjectName.includes(lowerTerm) || instrumentName.includes(lowerTerm) || clase.DiaSemana.toLowerCase().includes(lowerTerm)
            );
        });
    };

    const handleSearch = (searchTerm) => {
        setSearchTerm(searchTerm);
        setFilteredClasses(filterClassesByTerm(classes, searchTerm));
    };

    const handleAdd = () => {
        setSelectedClass(null);
        setIsModalOpen(true);
    };

    const handleEdit = (clase) => {
        setSelectedClass(clase);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar esta clase?')) {
            try {
                await clasesAPI.delete(id);
                toast.success('Clase eliminada correctamente');
                fetchData();
            } catch (err) {
                toast.error('Error al eliminar la clase');
                console.error(err);
            }
        }
    };

    const handleDuplicate = (clase) => {
        setClassToDuplicate(clase);
        setIsDuplicateModalOpen(true);
    };

    const handleDuplicateSubmit = async (newDay) => {
        try {
            if (!classToDuplicate) 
                return;
            
            const hasConflict = hasTeacherScheduleConflict(
                classToDuplicate.ProfesorID,
                newDay,
                classToDuplicate.H_Inicio,
                classToDuplicate.H_Final,
            );

            if (hasConflict) {
                toast.error('El profesor ya tiene otra clase en ese día y horario');
                return;
            }

            // Obtener los alumnos de la clase original
            const studentsRes = await clasesAlumnosAPI.getByClaseId(
                classToDuplicate.IDClase
            );
            const students = studentsRes.data;

            // Crear la nueva clase con el nuevo día
            const newClassData = {
                Curso: classToDuplicate.Curso,
                AsignaturaID: classToDuplicate.AsignaturaID,
                ProfesorID: classToDuplicate.ProfesorID,
                InstrumentoID: classToDuplicate.InstrumentoID || null,
                DiaSemana: newDay,
                H_Inicio: classToDuplicate.H_Inicio,
                H_Final: classToDuplicate.H_Final
            };

            const newClassRes = await clasesAPI.create(newClassData);
            const newClassId = newClassRes.data.id;

            // Añadir los mismos alumnos a la nueva clase
            if (students && students.length > 0) {
                await Promise.all(
                    students.map(student => clasesAlumnosAPI.addAlumnoAClase(newClassId, student.AlumnoID))
                );
            }

            toast.success('Clase duplicada correctamente');
            setIsDuplicateModalOpen(false);
            fetchData();
        } catch (err) {
            toast.error('Error al duplicar la clase');
            console.error(err);
        }
    };

    const handleSubmit = async (formData) => {
        try {
            const classData = {
                ...formData,
                Curso: currentYear,
                InstrumentoID: formData.InstrumentoID || null
            };

            const hasConflict = hasTeacherScheduleConflict(
                classData.ProfesorID,
                classData.DiaSemana,
                classData.H_Inicio,
                classData.H_Final,
                selectedClass?.IDClase || null
            );

            if (hasConflict) {
                toast.error('El profesor ya tiene otra clase en ese día y horario');
                return;
            }

            if (selectedClass) {
                await clasesAPI.update(selectedClass.IDClase, classData);
                toast.success('Clase actualizada correctamente');
            } else {
                await clasesAPI.create(classData);
                toast.success('Clase creada correctamente');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (err) {
            toast.error('Error al guardar la clase');
            console.error(err);
        }
    };

    const columns = [
        {
            key: 'AsignaturaID',
            label: 'Asignatura',
            sortable: true,
            render: (value) => subjects[value] || '-'
        }, {
            key: 'ProfesorID',
            label: 'Profesor',
            sortable: true,
            render: (value) => professors[value] || '-'
        }, {
            key: 'InstrumentoID',
            label: 'Instrumento',
            sortable: true,
            render: (value) => instruments[value] || '-'
        }, {
            key: 'DiaSemana',
            label: 'Día',
            sortable: true,
            render: (value) => getDayName(value)
        }, {
            key: 'H_Inicio',
            label: 'Hora Inicio',
            render: (value) => formatTime(value)
        }, {
            key: 'H_Final',
            label: 'Hora Fin',
            render: (value) => formatTime(value)
        }
    ];

    // Agregar columna de acciones solo si no es readonly
    if (!isReadOnly()) {
        columns.push({
            key: 'actions',
            label: 'Acciones',
            render: (_, clase) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleEdit(clase)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Editar">
                        <PencilIcon className="h-5 w-5"/>
                    </button>
                    <button
                        onClick={() => handleDuplicate(clase)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Duplicar">
                        <DocumentDuplicateIcon className="h-5 w-5"/>
                    </button>
                    <button
                        onClick={() => handleDelete(clase.IDClase)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar">
                        <TrashIcon className="h-5 w-5"/>
                    </button>
                </div>
            )
        });
    }

    if (loading) 
        return <LoadingSpinner text="Cargando clases..."/>;
    if (error) 
        return <ErrorMessage message={error} onRetry={fetchData}/>;
    
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Clases</h1>
                    <p className="text-gray-600">Curso {currentYear}/{currentYear + 1}</p>
                </div>
                {
                    !isReadOnly() && (
                        <button onClick={handleAdd} className="btn-primary">
                            <PlusIcon className="h-5 w-5 mr-2"/>
                            Nueva Clase
                        </button>
                    )
                }
            </div>

            <SearchFilter
                onSearch={handleSearch}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Buscar por profesor, asignatura, instrumento o día..."/>

            <div className="card">
                <DataTable
                    columns={columns}
                    data={filteredClasses}
                    emptyMessage="No hay clases programadas"/>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={selectedClass
                    ? 'Editar Clase'
                    : 'Nueva Clase'}
                size="lg">
                <ClassForm
                    classData={selectedClass}
                    onSubmit={handleSubmit}
                    onCancel={() => setIsModalOpen(false)}
                    currentYear={currentYear}/>
            </Modal>

            <Modal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                title="Duplicar Clase"
                size="md">
                <DuplicateClassForm
                    classData={classToDuplicate}
                    onSubmit={handleDuplicateSubmit}
                    onCancel={() => setIsDuplicateModalOpen(false)}/>
            </Modal>
        </div>
    );
};

// Componente de formulario para duplicar clase
const DuplicateClassForm = ({classData, onSubmit, onCancel}) => {
    const [selectedDay, setSelectedDay] = useState('1');

    const daysOfWeek = [
        {
            value: '1',
            label: 'Lunes'
        }, {
            value: '2',
            label: 'Martes'
        }, {
            value: '3',
            label: 'Miércoles'
        }, {
            value: '4',
            label: 'Jueves'
        }, {
            value: '5',
            label: 'Viernes'
        }
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        if (
            selectedDay === classData
                ?.DiaSemana
        ) {
            toast.error('Debe seleccionar un día diferente al de la clase original');
            return;
        }
        onSubmit(selectedDay);
    };

    if (!classData) 
        return null;
    
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Clase a duplicar:</h3>
                <p className="text-sm text-gray-600">
                    <strong>Día actual:</strong>
                    {getDayName(classData.DiaSemana)}
                </p>
                <p className="text-sm text-gray-600">
                    <strong>Horario:</strong>
                    {formatTime(classData.H_Inicio)}
                    - {formatTime(classData.H_Final)}
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nuevo día de la semana *
                </label>
                <select
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                    {
                        daysOfWeek.map(day => (
                            <option
                                key={day.value}
                                value={day.value}
                                disabled={day.value === classData.DiaSemana}>
                                {day.label}
                                {
                                    day.value === classData.DiaSemana
                                        ? '(actual)'
                                        : ''
                                }
                            </option>
                        ))
                    }
                </select>
            </div>

            <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                ℹ️ La nueva clase se creará con los mismos datos (profesor, asignatura, horario)
                y alumnos que la clase original.
            </div>

            <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={onCancel} className="btn-secondary">
                    Cancelar
                </button>
                <button type="submit" className="btn-primary">
                    Duplicar Clase
                </button>
            </div>
        </form>
    );
};

export default Classes;
